import {
  NominatimResult,
  OverpassResponse,
  OverpassElement,
  BuildingFootprintResult,
  GeoJSONPolygon,
  OSMBuildingProperties,
} from '@/types/osm';

// Nominatim API base URL
const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';

// Overpass API base URL
const OVERPASS_BASE_URL = 'https://overpass-api.de/api/interpreter';

/**
 * Geocode an address using OSM Nominatim
 * @param address - Address string to geocode
 * @returns Array of geocoding results
 */
export async function geocodeAddress(
  address: string
): Promise<NominatimResult[]> {
  try {
    const params = new URLSearchParams({
      q: address,
      format: 'json',
      addressdetails: '1',
      limit: '5',
    });

    const response = await fetch(
      `${NOMINATIM_BASE_URL}/search?${params.toString()}`,
      {
        headers: {
          'User-Agent': 'UrbanMix-BuildingCreation/1.0', // Required by Nominatim
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.statusText}`);
    }

    const results: NominatimResult[] = await response.json();
    return results;
  } catch (error) {
    console.error('Error geocoding address:', error);
    throw error;
  }
}

/**
 * Search for address suggestions (autocomplete)
 * @param query - Partial address query
 * @returns Array of address suggestions
 */
export async function searchAddresses(
  query: string
): Promise<NominatimResult[]> {
  if (query.length < 3) {
    return [];
  }

  return geocodeAddress(query);
}

/**
 * Convert OSM way geometry to GeoJSON polygon
 * @param element - Overpass element with geometry
 * @returns GeoJSON Polygon
 */
function convertToGeoJSONPolygon(element: OverpassElement): GeoJSONPolygon {
  if (!element.geometry) {
    throw new Error('Element has no geometry');
  }

  const coordinates: [number, number][] = element.geometry.map((point) => [point.lon, point.lat] as [number, number]);

  // Close the polygon if it's not already closed
  const first = coordinates[0];
  const last = coordinates[coordinates.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    coordinates.push([first[0], first[1]]);
  }

  return {
    type: 'Polygon',
    coordinates: [coordinates],
  };
}

/**
 * Extract building properties from OSM tags
 * @param tags - OSM element tags
 * @returns Building properties for visualization
 */
function extractBuildingProperties(
  tags: Record<string, string> = {}
): OSMBuildingProperties {
  const properties: OSMBuildingProperties = {};

  // Extract height
  if (tags['height']) {
    const height = parseFloat(tags['height']);
    if (!isNaN(height)) {
      properties.height = height;
    }
  } else if (tags['building:height']) {
    const height = parseFloat(tags['building:height']);
    if (!isNaN(height)) {
      properties.height = height;
    }
  } else if (tags['building:levels'] || tags['levels']) {
    // Estimate height from levels (assume 3m per level)
    const levels = parseInt(tags['building:levels'] || tags['levels']);
    if (!isNaN(levels)) {
      properties.levels = levels;
      properties.height = levels * 3;
    }
  }

  // Extract min height
  if (tags['min_height']) {
    const minHeight = parseFloat(tags['min_height']);
    if (!isNaN(minHeight)) {
      properties.minHeight = minHeight;
    }
  }

  // Extract color
  if (tags['building:colour'] || tags['building:color']) {
    properties.color = tags['building:colour'] || tags['building:color'];
  }

  // Extract roof color
  if (tags['roof:colour'] || tags['roof:color']) {
    properties.roofColor = tags['roof:colour'] || tags['roof:color'];
  }

  // Extract roof shape
  if (tags['roof:shape']) {
    properties.roofShape = tags['roof:shape'];
  }

  // Extract material
  if (tags['building:material']) {
    properties.material = tags['building:material'];
  }

  // Extract building name
  if (tags['name']) {
    properties.name = tags['name'];
  }

  // Extract building type
  if (tags['building'] && tags['building'] !== 'yes') {
    properties.type = tags['building'];
  }

  return properties;
}

/**
 * Get building footprint at given coordinates using Overpass API
 * @param lat - Latitude
 * @param lon - Longitude
 * @param radius - Search radius in meters (default 50m)
 * @returns Building footprint result
 */
export async function getBuildingFootprint(
  lat: number,
  lon: number,
  radius: number = 50
): Promise<BuildingFootprintResult> {
  try {
    // Overpass QL query to find buildings near the coordinates
    const query = `
      [out:json];
      (
        way["building"](around:${radius},${lat},${lon});
        relation["building"](around:${radius},${lat},${lon});
      );
      out geom;
    `;

    const response = await fetch(OVERPASS_BASE_URL, {
      method: 'POST',
      body: query,
    });

    if (!response.ok) {
      throw new Error(`Overpass API error: ${response.statusText}`);
    }

    const data: OverpassResponse = await response.json();

    if (data.elements.length === 0) {
      // No building found
      return {
        found: false,
        footprint: null,
        properties: {},
        coordinates: { lat, lon },
      };
    }

    // Use the first building found (closest to the point)
    const building = data.elements[0];
    const properties = extractBuildingProperties(building.tags);

    let footprint: GeoJSONPolygon | null = null;

    if (building.geometry) {
      footprint = convertToGeoJSONPolygon(building);
    }

    return {
      found: true,
      footprint,
      properties,
      coordinates: { lat, lon },
    };
  } catch (error) {
    console.error('Error fetching building footprint:', error);
    throw error;
  }
}

/**
 * Create a default rectangular footprint when no OSM data is available
 * @param lat - Center latitude
 * @param lon - Center longitude
 * @param width - Width in meters (default 20m)
 * @param length - Length in meters (default 30m)
 * @returns Default building footprint
 */
export function createDefaultFootprint(
  lat: number,
  lon: number,
  width: number = 20,
  length: number = 30
): BuildingFootprintResult {
  // Rough conversion: 1 degree latitude ≈ 111km, 1 degree longitude varies by latitude
  const latOffset = (length / 2 / 111000); // Half length in degrees
  const lonOffset = (width / 2 / (111000 * Math.cos(lat * Math.PI / 180))); // Half width in degrees

  const footprint: GeoJSONPolygon = {
    type: 'Polygon',
    coordinates: [
      [
        [lon - lonOffset, lat + latOffset], // NW
        [lon + lonOffset, lat + latOffset], // NE
        [lon + lonOffset, lat - latOffset], // SE
        [lon - lonOffset, lat - latOffset], // SW
        [lon - lonOffset, lat + latOffset], // Close polygon
      ],
    ],
  };

  return {
    found: false, // Indicates this is a default footprint
    footprint,
    properties: {
      height: 12, // Default 4 floors × 3m
      minHeight: 0,
    },
    coordinates: { lat, lon },
  };
}
